import { ZapperGraphQLClient, ZapperGraphQLConfig } from './zapperGraphQL';
import { elizaLogger } from '@elizaos/core';

/**
 * Singleton pattern for ZapperGraphQLClient to ensure performance optimizations
 * are shared across all actions and providers
 */
class ZapperClientManager {
  private static instance: ZapperClientManager;
  private client: ZapperGraphQLClient | null = null;
  private config: ZapperGraphQLConfig | null = null;

  private constructor() {
    // Private constructor for singleton pattern
  }

  public static getInstance(): ZapperClientManager {
    if (!ZapperClientManager.instance) {
      ZapperClientManager.instance = new ZapperClientManager();
    }
    return ZapperClientManager.instance;
  }

  /**
   * Initialize the client with performance-optimized configuration
   */
  public initializeClient(apiKey: string, apiUrl?: string): ZapperGraphQLClient {
    const config: ZapperGraphQLConfig = {
      apiKey,
      apiUrl: apiUrl || 'https://public.zapper.xyz/graphql',
      timeout: 30000,
      retryAttempts: 3,
      retryDelay: 1000,
      debug: false,
      enableCaching: true,
      cacheConfig: {
        maxSize: 2000, // Increased cache size for better performance
        defaultTTL: 300000, // 5 minutes default TTL
        cleanupInterval: 60000, // 1 minute cleanup
      },
      errorHandlerConfig: {
        // maxAttempts: 3, // Removed as it's not in the interface
        maxDelay: 10000,
        backoffMultiplier: 2,
        jitter: true,
        enableCircuitBreaker: true,
        circuitBreakerConfig: {
          failureThreshold: 5,
          resetTimeout: 30000,
          monitoringWindow: 60000,
        },
      },
      rateLimitConfig: {
        algorithm: 'TOKEN_BUCKET' as any,
        maxRequests: 100, // Conservative rate limit
        windowMs: 60000, // 1 minute window
        enableQueue: true,
        queueSize: 50,
        enableMetrics: true,
        burstLimit: 10, // Allow bursts up to 10 requests
        refillRate: 50, // Refill 50 tokens per window
      },
      quotaConfig: {
        dailyQuota: 10000, // 10k requests per day
        monthlyQuota: 100000, // 100k requests per month
        enableMetrics: true,
      },
    };

    this.config = config;
    this.client = new ZapperGraphQLClient(config);

    elizaLogger.info('ZapperGraphQLClient initialized with performance optimizations', {
      cacheEnabled: config.enableCaching,
      rateLimitEnabled: !!config.rateLimitConfig,
      quotaEnabled: !!config.quotaConfig,
    });

    return this.client;
  }

  /**
   * Get the client instance
   */
  public getClient(): ZapperGraphQLClient {
    if (!this.client) {
      throw new Error('ZapperGraphQLClient not initialized. Call initializeClient() first.');
    }
    return this.client;
  }

  /**
   * Check if client is initialized
   */
  public isInitialized(): boolean {
    return this.client !== null;
  }

  /**
   * Get performance statistics
   */
  public getPerformanceStats(): any {
    if (!this.client) {
      return null;
    }

    return {
      clientStats: this.client.getStats(),
      rateLimitStatus: this.client.getRateLimitStatus(),
      errorMetrics: this.client.getErrorMetrics(),
      circuitBreakerStatus: this.client.getCircuitBreakerStatus(),
    };
  }

  /**
   * Optimize cache for better performance
   */
  public optimizeCache(): { entriesRemoved: number; memoryFreed: number } {
    if (!this.client) {
      return { entriesRemoved: 0, memoryFreed: 0 };
    }

    const result = this.client.optimizeCache();
    elizaLogger.debug('Cache optimization completed', result);
    return result;
  }

  /**
   * Update configuration for performance tuning
   */
  public updateConfig(updates: Partial<ZapperGraphQLConfig>): void {
    if (!this.client) {
      throw new Error('ZapperGraphQLClient not initialized');
    }

    this.client.updateConfig(updates);
    elizaLogger.debug('ZapperGraphQLClient configuration updated', updates);
  }

  /**
   * Reset performance counters
   */
  public resetMetrics(): void {
    if (!this.client) {
      return;
    }

    this.client.resetErrorMetrics();
    this.client.resetRateLimits();
    elizaLogger.debug('ZapperGraphQLClient metrics reset');
  }

  /**
   * Preload common queries for better performance
   */
  public preloadCommonQueries(): void {
    if (!this.client) {
      return;
    }

    elizaLogger.debug('Preloading common queries for performance optimization');

    // Note: Zapper API doesn't support a general 'networks' query
    // Individual network info can be queried with: network(chainId: Int!, vm: VirtualMachineType!)
    // Removed broken Networks query that was causing 400 errors during client initialization
  }

  /**
   * Start performance monitoring
   */
  public startPerformanceMonitoring(): void {
    if (!this.client) {
      return;
    }

    // Log performance stats every 5 minutes
    setInterval(() => {
      const stats = this.getPerformanceStats();
      if (stats) {
        elizaLogger.debug('ZapperGraphQLClient performance stats', {
          requestCount: stats.clientStats.requestCount,
          avgRequestTime: stats.clientStats.avgRequestTime,
          cacheHitRate: stats.clientStats.cacheStats?.hitRate || 0,
          rateLimitRemaining: stats.rateLimitStatus?.rateLimit?.remaining || 0,
          errorRate: stats.errorMetrics?.errorRate || 0,
        });
      }
    }, 300000); // 5 minutes

    // Optimize cache every 10 minutes
    setInterval(() => {
      this.optimizeCache();
    }, 600000); // 10 minutes

    elizaLogger.info('ZapperGraphQLClient performance monitoring started');
  }

  /**
   * Destroy the client and cleanup resources
   */
  public destroy(): void {
    if (this.client) {
      this.client.destroy();
      this.client = null;
      this.config = null;
    }
    elizaLogger.info('ZapperGraphQLClient destroyed');
  }
}

/**
 * Helper function to get the ZapperGraphQLClient instance
 */
export function getZapperClient(): ZapperGraphQLClient {
  return ZapperClientManager.getInstance().getClient();
}

/**
 * Helper function to initialize the ZapperGraphQLClient
 */
export function initializeZapperClient(apiKey: string, apiUrl?: string): ZapperGraphQLClient {
  const manager = ZapperClientManager.getInstance();
  const client = manager.initializeClient(apiKey, apiUrl);

  // Start performance monitoring
  manager.startPerformanceMonitoring();

  // Preload common queries
  manager.preloadCommonQueries();

  return client;
}

/**
 * Helper function to check if client is initialized
 */
export function isZapperClientInitialized(): boolean {
  return ZapperClientManager.getInstance().isInitialized();
}

/**
 * Helper function to get performance statistics
 */
export function getZapperPerformanceStats(): any {
  return ZapperClientManager.getInstance().getPerformanceStats();
}

/**
 * Helper function to optimize cache
 */
export function optimizeZapperCache(): { entriesRemoved: number; memoryFreed: number } {
  return ZapperClientManager.getInstance().optimizeCache();
}

/**
 * Helper function to update configuration
 */
export function updateZapperConfig(updates: Partial<ZapperGraphQLConfig>): void {
  ZapperClientManager.getInstance().updateConfig(updates);
}

/**
 * Helper function to reset metrics
 */
export function resetZapperMetrics(): void {
  ZapperClientManager.getInstance().resetMetrics();
}

/**
 * Helper function to destroy client
 */
export function destroyZapperClient(): void {
  ZapperClientManager.getInstance().destroy();
}

export default {
  getZapperClient,
  initializeZapperClient,
  isZapperClientInitialized,
  getZapperPerformanceStats,
  optimizeZapperCache,
  updateZapperConfig,
  resetZapperMetrics,
  destroyZapperClient,
};
